import React from "react";
import { Box, Typography } from "@mui/material";
import { FormatFullName } from "app/utils/helper";
import moment from "moment";

const ModernCertificate = ({
  student,
  schoolDetail,
  selectedSession,
  checkBoxEnabled = {},
  certificateTitle = "Certificate of Achievement",
  certificateSubtitle = "This is to certify that",
  courseName = "Academic Excellence",
  description = "Has successfully completed the academic requirements",
  primaryColor = "#1e3a8a",
  secondaryColor = "#d4af37",
  accentColor = "#991b1b",
  fontFamily = "Georgia",
  pageOrientation = "landscape",
  fieldStyles = {},
}) => {
  const studentFullName = FormatFullName(
    student?.firstName,
    student?.middleName,
    student?.lastName
  );

  const currentDate = moment().format("DD MMMM, YYYY");
  const certificateNumber = `CERT-${selectedSession}-${
    student?.admissionNumber ||
    student?.srNumber ||
    Math.floor(Math.random() * 10000)
  }`;

  const isLandscape = pageOrientation === "landscape";

  return (
    <Box
      sx={{
        width: isLandscape ? "297mm" : "210mm",
        height: isLandscape ? "210mm" : "297mm",
        margin: "0 auto",
        padding: "0",
        fontFamily: fontFamily || "Georgia",
        backgroundColor: "#ffffff",
        position: "relative",
        boxSizing: "border-box",
      }}
    >
      {/* Side Accent Bars */}
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: "15px",
          backgroundColor: primaryColor,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          right: 0,
          top: 0,
          bottom: 0,
          width: "15px",
          backgroundColor: secondaryColor,
        }}
      />

      <Box
        sx={{
          padding: isLandscape ? "20px 65px" : "40px 30px",
          position: "relative",
          height: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        {/* Header with Logo */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: isLandscape ? "15px" : "20px",
            gap: isLandscape ? "25px" : "15px",
          }}
        >
          {checkBoxEnabled.schoolLogo && (
            <Box>
              <img
                src={schoolDetail?.schoolLogo}
                alt="School Logo"
                style={{
                  height: isLandscape ? "90px" : "70px",
                  width: isLandscape ? "90px" : "70px",
                  objectFit: "contain",
                }}
              />
            </Box>
          )}

          <Box sx={{ flex: 1, textAlign: "left" }}>
            {checkBoxEnabled.schoolName && (
              <Typography
                sx={{
                  fontSize: isLandscape ? "34px" : "28px",
                  fontWeight: 700,
                  color: "#1f2937",
                  fontFamily: fontFamily,
                  marginBottom: "8px",
                }}
              >
                {schoolDetail?.schoolName || "School Name"}
              </Typography>
            )}

            {checkBoxEnabled.schoolAddress && (
              <Typography
                sx={{
                  fontSize: isLandscape ? "16px" : "14px",
                  color: "#6b7280",
                  fontFamily: fontFamily,
                  width: "100%",
                  alignItems: "center",
                }}
              >
                {schoolDetail?.schoolAddress?.address ?? "School Address"}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Certificate Badge */}
        {checkBoxEnabled.certificateTitle && (
          <Box
            sx={{
              backgroundColor: primaryColor,
              padding: isLandscape ? "18px 45px" : "12px 25px",
              textAlign: "center",
              marginBottom: isLandscape ? "15px" : "25px",
              position: "relative",
            }}
          >
            <Typography
              sx={{
                fontSize: `${
                  fieldStyles.certificateTitle?.fontSize ||
                  (isLandscape ? 46 : 36)
                }px`,
                fontWeight: fieldStyles.certificateTitle?.fontWeight || 700,
                color: fieldStyles.certificateTitle?.color || "#ffffff",
                fontFamily:
                  fieldStyles.certificateTitle?.fontFamily || fontFamily,
                fontStyle: fieldStyles.certificateTitle?.fontStyle || "normal",
                textDecoration:
                  fieldStyles.certificateTitle?.textDecoration || "none",
                letterSpacing: "2px",
                textTransform: "uppercase",
              }}
            >
              {certificateTitle}
            </Typography>
          </Box>
        )}

        {/* Main Content */}
        <Box
          sx={{
            textAlign: "center",
            marginBottom: isLandscape ? "20px" : "25px",
          }}
        >
          {checkBoxEnabled.certificateSubtitle && (
            <Typography
              sx={{
                fontSize: `${
                  fieldStyles.certificateSubtitle?.fontSize ||
                  (isLandscape ? 20 : 18)
                }px`,
                fontWeight:
                  fieldStyles.certificateSubtitle?.fontWeight || "normal",
                color: fieldStyles.certificateSubtitle?.color || "#6b7280",
                fontFamily:
                  fieldStyles.certificateSubtitle?.fontFamily || fontFamily,
                fontStyle:
                  fieldStyles.certificateSubtitle?.fontStyle || "italic",
                textDecoration:
                  fieldStyles.certificateSubtitle?.textDecoration || "none",
                marginBottom: isLandscape ? "10px" : "15px",
              }}
            >
              {certificateSubtitle}
            </Typography>
          )}

          {checkBoxEnabled.studentName && (
            <Box
              sx={{
                marginBottom: isLandscape ? "14px" : "20px",
              }}
            >
              <Typography
                sx={{
                  fontSize: `${
                    fieldStyles.studentName?.fontSize || (isLandscape ? 48 : 36)
                  }px`,
                  fontWeight: fieldStyles.studentName?.fontWeight || 700,
                  color: fieldStyles.studentName?.color || primaryColor,
                  fontFamily: fieldStyles.studentName?.fontFamily || fontFamily,
                  fontStyle: fieldStyles.studentName?.fontStyle || "normal",
                  textDecoration:
                    fieldStyles.studentName?.textDecoration || "none",
                  marginBottom: "5px",
                }}
              >
                {studentFullName}
              </Typography>
              <Box
                sx={{
                  width: isLandscape ? "350px" : "250px",
                  height: "4px",
                  backgroundColor: secondaryColor,
                  margin: "0 auto",
                }}
              />
            </Box>
          )}

          {checkBoxEnabled.class && (
            <Box
              sx={{
                fontSize: isLandscape ? "18px" : "16px",
                color: "#4b5563",
                fontFamily: fontFamily,
                marginBottom: isLandscape ? "15px" : "15px",
              }}
            >
              <Box
                component="span"
                sx={{ fontWeight: 600, marginRight: "10px" }}
              >
                Class:
              </Box>
              <Box component="span" sx={{ fontWeight: 400 }}>
                {student?.className || "N/A"} - {student?.section || "N/A"}
              </Box>
            </Box>
          )}

          {checkBoxEnabled.courseName && (
            <Box
              sx={{
                backgroundColor: `${accentColor}10`,
                border: `3px solid ${accentColor}`,
                borderRadius: "10px",
                padding: isLandscape ? "9px 35px" : "12px 20px",
                margin: isLandscape ? "9px auto" : "15px auto",
                maxWidth: "700px",
              }}
            >
              <Typography
                sx={{
                  fontSize: `${
                    fieldStyles.courseName?.fontSize || (isLandscape ? 26 : 22)
                  }px`,
                  fontWeight: fieldStyles.courseName?.fontWeight || 600,
                  color: fieldStyles.courseName?.color || "#1f2937",
                  fontFamily: fieldStyles.courseName?.fontFamily || fontFamily,
                  fontStyle: fieldStyles.courseName?.fontStyle || "normal",
                  textDecoration:
                    fieldStyles.courseName?.textDecoration || "none",
                }}
              >
                {courseName}
              </Typography>
            </Box>
          )}

          {checkBoxEnabled.description && (
            <Typography
              sx={{
                fontSize: `${
                  fieldStyles.description?.fontSize || (isLandscape ? 19 : 16)
                }px`,
                fontWeight: fieldStyles.description?.fontWeight || "normal",
                color: fieldStyles.description?.color || "#4b5563",
                fontFamily: fieldStyles.description?.fontFamily || fontFamily,
                fontStyle: fieldStyles.description?.fontStyle || "normal",
                textDecoration:
                  fieldStyles.description?.textDecoration || "none",
                lineHeight: 1.7,
                maxWidth: "85%",
                margin: isLandscape ? "25px auto" : "15px auto",
              }}
            >
              {description}
            </Typography>
          )}

          {checkBoxEnabled.academicSession && (
            <Typography
              sx={{
                fontSize: isLandscape ? "20px" : "16px",
                color: "#374151",
                fontFamily: fontFamily,
                marginTop: isLandscape ? "28px" : "20px",
                "& strong": {
                  color: primaryColor,
                  fontWeight: 700,
                },
              }}
            >
              Academic Session: <strong>{selectedSession}</strong>
            </Typography>
          )}
        </Box>

        {/* Footer Signatures */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            marginTop: "auto",
            paddingTop: "30px",
            marginBottom: "25px",
          }}
        >

          <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
            {checkBoxEnabled.principalSignature && (
              <Box sx={{ textAlign: "center", minWidth: "190px" }}>
                <Box
                  sx={{
                    width: "190px",
                    height: "3px",
                    backgroundColor: "#000",
                    marginBottom: "10px",
                  }}
                />
                {/* <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#1f2937",
                    fontFamily: fontFamily,
                    marginBottom: "3px",
                  }}
                >
                  {principalName}
                </Typography> */}
                <Typography
                  sx={{
                    fontSize: "16px",
                    color: "#6b7280",
                    fontFamily: fontFamily,
                    fontStyle: "italic",
                  }}
                >
                  Principal
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
            {checkBoxEnabled.coordinatorSignature && (
              <Box sx={{ textAlign: "center", minWidth: "190px" }}>
                <Box
                  sx={{
                    width: "190px",
                    height: "3px",
                    backgroundColor: "#000",
                    marginBottom: "10px",
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "16px",
                    color: "#6b7280",
                    fontFamily: fontFamily,
                    fontStyle: "italic",
                  }}
                >
                  Academic Coordinator
                </Typography>
              </Box>
            )}
          </Box>

          {/* {checkBoxEnabled.seal && (
            <Box
              sx={{
                width: "115px",
                height: "115px",
                borderRadius: "50%",
                border: `5px solid ${primaryColor}`,
                backgroundColor: `${secondaryColor}15`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  width: "80px",
                  height: "80px",
                  borderRadius: "50%",
                  border: "3px dashed rgba(0, 0, 0, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 700,
                    textAlign: "center",
                    lineHeight: 1.4,
                    color: "#1f2937",
                  }}
                >
                  OFFICIAL
                  <br />
                  SEAL
                </Typography>
              </Box>
            </Box>
          )} */}

          
        </Box>

        {/* Bottom Info Bar */}
        <Box
          sx={{
            borderTop: `4px solid ${primaryColor}`,
            paddingTop: "18px",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          {/* {checkBoxEnabled.certificateNumber && (
            <Typography
              sx={{
                fontSize: "14px",
                color: "#6b7280",
                fontFamily: fontFamily,
              }}
            >
              Certificate No: {certificateNumber}
            </Typography>
          )} */}
          {checkBoxEnabled.date && (
            <Typography
              sx={{
                fontSize: "14px",
                color: "#6b7280",
                fontFamily: fontFamily,
              }}
            >
              Issue Date: {currentDate}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ModernCertificate;
